import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { RouterModule } from "@angular/router";
import { rxResource } from "@angular/core/rxjs-interop";
import { ApiService } from "../core/api.service";
import { catchError, of } from "rxjs";

@Component({
  selector: "app-catalog-settings-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule],
  template: `
    <section class="dashboard">
      <div class="dashboard-main">
        <header class="dashboard-header">
          <div>
            <h1>Blinds Catalog</h1>
            <p class="header-subtitle">
              Manage blind categories and blinds.
            </p>
          </div>
        </header>

        <div class="settings-grid">
          <a class="settings-card" routerLink="/catalog/categories">
            <div class="settings-card-icon">🏷️</div>
            <div class="settings-card-title">Categories</div>
            <div class="settings-card-desc">
              {{ categoriesCount()?.length ?? 0 }} categor{{ categoriesCount()?.length === 1 ? "y" : "ies" }} · group blinds by product family.
            </div>
          </a>

          <a class="settings-card" routerLink="/catalog/blinds">
            <div class="settings-card-icon">🪟</div>
            <div class="settings-card-title">Blinds</div>
            <div class="settings-card-desc">
              {{ blindsCount()?.length ?? 0 }} blind{{ blindsCount()?.length === 1 ? "" : "s" }} · codes, images, and tags.
            </div>
          </a>
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      .dashboard { display: grid; grid-template-columns: 1fr; gap: 24px; align-items: start; }
      .dashboard-main { min-width: 0; }
      .dashboard-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 16px; }
      .dashboard-header h1 { font-size: 32px; margin: 0; }
      .header-subtitle { color: var(--ink-500); margin: 4px 0 0; font-size: 14px; }
      .header-actions { display: flex; gap: 10px; align-items: center; }
      .action-button {
        border: none; background: var(--panel); color: var(--ink-900); padding: 10px 16px;
        border-radius: 12px; font-weight: 600; font-size: 14px; cursor: pointer;
        box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08); text-decoration: none;
        display: inline-flex; align-items: center;
      }
      .settings-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 20px; }
      .settings-card {
        display: block; background: var(--panel); border-radius: 20px; padding: 24px;
        box-shadow: 0 14px 32px rgba(15, 23, 42, 0.1); text-decoration: none;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
      }
      .settings-card:hover { transform: translateY(-2px); box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16); }
      .settings-card-icon { font-size: 28px; }
      .settings-card-title { font-size: 17px; font-weight: 700; color: var(--ink-900); margin-top: 12px; }
      .settings-card-desc { font-size: 13px; color: var(--ink-500); margin-top: 6px; line-height: 1.5; }
    `,
  ],
})
export class CatalogSettingsPageComponent {
  private readonly api = inject(ApiService);

  readonly categoriesResource = rxResource({
    stream: () =>
      this.api.getCategories().pipe(catchError(() => of([] as any[]))),
  });
  readonly blindsResource = rxResource({
    stream: () =>
      this.api.getCatalogBlinds().pipe(catchError(() => of([] as any[]))),
  });

  readonly categoriesCount = this.categoriesResource.value;
  readonly blindsCount = this.blindsResource.value;
}
